import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  getAdminQueueStats,
  getAdminFailedJobs,
  retryAdminJob,
  deleteAdminJob,
  retryAllAdminFailedJobs,
  deleteAllAdminFailedJobs,
} from 'API/linkedin-funnel';
import AdminSubpageHeader from 'features/admin/admin-subpage-header';
import { Roles } from 'enums';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiButton from 'features/ui/base-components/pai-button';
import { useUserData } from 'hooks/use-user-data';

const QUEUE_NAMES = [
  { key: 'linkedin-search', label: 'Search' },
  { key: 'linkedin-enrichment', label: 'Enrichment' },
  { key: 'linkedin-judging', label: 'Judging' },
  { key: 'linkedin-campaigns', label: 'Campaigns' },
  { key: 'linkedin-inbox', label: 'Inbox' },
  { key: 'linkedin-login', label: 'Login' },
  { key: 'linkedin-cookies', label: 'Cookies' },
] as const;

type QueueKey = (typeof QUEUE_NAMES)[number]['key'];

interface QueueStats {
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  delayed: number;
}

interface FailedJob {
  id: string;
  name: string;
  data: {
    type?: string;
    tenantId?: string;
    campaignId?: string;
    accountId?: string;
    searchTitle?: string;
    searchId?: string;
    contactId?: string;
  };
  failedReason: string;
  stacktrace: string[];
  attemptsMade: number;
  timestamp: number;
  finishedOn: number;
  processedOn: number;
}

const AdminQueueManagement: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { role } = useUserData();

  const [stats, setStats] = useState<Record<QueueKey, QueueStats> | null>(null);
  const [selectedQueue, setSelectedQueue] = useState<QueueKey>('linkedin-search');
  const [jobs, setJobs] = useState<FailedJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: 'retryAll' | 'deleteAll';
    queue: QueueKey;
  } | null>(null);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await getAdminQueueStats();
      if (data) setStats(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load queue stats');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchJobs = useCallback(async (queue: QueueKey) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminFailedJobs(queue, 0, 200);
      if (data) setJobs(data.jobs || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load failed jobs');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchJobs(selectedQueue);
  }, [selectedQueue, fetchJobs]);

  const handleRetry = async (jobId: string) => {
    setActionLoading(jobId);
    try {
      await retryAdminJob(selectedQueue, jobId);
      await fetchJobs(selectedQueue);
      await fetchStats();
    } catch (e: any) {
      setError(e?.message || 'Failed to retry job');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (jobId: string) => {
    setActionLoading(jobId);
    try {
      await deleteAdminJob(selectedQueue, jobId);
      await fetchJobs(selectedQueue);
      await fetchStats();
    } catch (e: any) {
      setError(e?.message || 'Failed to delete job');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkAction = async () => {
    if (!confirmDialog) return;
    setActionLoading('bulk');
    try {
      if (confirmDialog.action === 'retryAll') {
        await retryAllAdminFailedJobs(confirmDialog.queue);
      } else {
        await deleteAllAdminFailedJobs(confirmDialog.queue);
      }
      await fetchJobs(selectedQueue);
      await fetchStats();
    } catch (e: any) {
      setError(e?.message || 'Bulk action failed');
    } finally {
      setActionLoading(null);
      setConfirmDialog(null);
    }
  };

  const totalFailed = useMemo(() => {
    if (!stats) return 0;
    return Object.values(stats).reduce((sum, s) => sum + (s?.failed || 0), 0);
  }, [stats]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'Job ID',
        flex: 1.5,
        minWidth: 180,
        renderCell: (params: GridRenderCellParams) => (
          <Tooltip title={params.value}>
            <Typography variant="body2" noWrap sx={{ fontFamily: 'monospace', fontSize: 12 }}>
              {params.value}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'type',
        headerName: 'Type',
        flex: 1,
        minWidth: 130,
        valueGetter: (_value: unknown, row: FailedJob) => row.data?.type || row.name || '-',
      },
      {
        field: 'context',
        headerName: 'Context',
        flex: 1.2,
        minWidth: 150,
        valueGetter: (_value: unknown, row: FailedJob) => {
          if (row.data?.searchTitle) return row.data.searchTitle;
          if (row.data?.campaignId) return `Campaign: ${row.data.campaignId.substring(0, 8)}...`;
          if (row.data?.contactId) return `Contact: ${row.data.contactId.substring(0, 8)}...`;
          return '-';
        },
      },
      {
        field: 'failedReason',
        headerName: 'Error',
        flex: 2,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams) => (
          <Tooltip title={params.value || ''}>
            <Typography variant="body2" noWrap sx={{ fontSize: 12, color: 'error.main' }}>
              {params.value || '-'}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'attemptsMade',
        headerName: 'Attempts',
        width: 80,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'finishedOn',
        headerName: 'Failed At',
        width: 160,
        valueGetter: (_value: unknown, row: FailedJob) =>
          row.finishedOn ? new Date(row.finishedOn).toLocaleString('de-DE') : '-',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Retry">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleRetry(params.row.id)}
                disabled={actionLoading === params.row.id}
              >
                <ReplayIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDelete(params.row.id)}
                disabled={actionLoading === params.row.id}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [actionLoading, selectedQueue],
  );

  if (role !== Roles.OWNER) {
    return (
      <div className={classes.root}>
        <Typography>You do not have permission to access this page.</Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <AdminSubpageHeader title={t('adminNavQueue')} />
      <Stack direction="row" alignItems="center" justifyContent="flex-end" mb={2}>
        <PaiButton
          text={t('refresh')}
          startIcon={<RefreshIcon />}
          action={() => { fetchStats(); fetchJobs(selectedQueue); }}
          disabled={loading || statsLoading}
        />
      </Stack>

      {error && (
        <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
          <Typography variant="body2">{error}</Typography>
        </Paper>
      )}

      {/* Queue chips with failed counts */}
      <Stack direction="row" spacing={1} flexWrap="wrap" mb={2} useFlexGap>
        {QUEUE_NAMES.map(({ key, label }) => {
          const failedCount = stats?.[key]?.failed || 0;
          return (
            <Chip
              key={key}
              label={`${label} ${failedCount > 0 ? `(${failedCount})` : ''}`}
              color={selectedQueue === key ? 'primary' : failedCount > 0 ? 'error' : 'default'}
              variant={selectedQueue === key ? 'filled' : 'outlined'}
              onClick={() => setSelectedQueue(key)}
              icon={failedCount > 0 ? <ErrorOutlineIcon /> : undefined}
            />
          );
        })}
        <Chip
          label={`Total Failed: ${totalFailed}`}
          color={totalFailed > 0 ? 'error' : 'default'}
          variant="filled"
          sx={{ fontWeight: 600 }}
        />
      </Stack>

      {/* Stats summary for selected queue */}
      {stats?.[selectedQueue] && (
        <Stack direction="row" spacing={2} mb={2}>
          {(['waiting', 'active', 'delayed', 'failed', 'completed'] as const).map((key) => (
            <Paper key={key} sx={{ px: 2, py: 1, textAlign: 'center', minWidth: 90 }}>
              <Typography variant="caption" color="text.secondary" textTransform="capitalize">
                {key}
              </Typography>
              <Typography
                variant="h6"
                fontWeight={600}
                color={key === 'failed' && stats[selectedQueue][key] > 0 ? 'error.main' : 'text.primary'}
              >
                {stats[selectedQueue][key]}
              </Typography>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Bulk actions */}
      {jobs.length > 0 && (
        <Stack direction="row" spacing={1} mb={2}>
          <PaiButton
            text={`Retry All (${jobs.length})`}
            startIcon={<ReplayIcon />}
            action={() => setConfirmDialog({ open: true, action: 'retryAll', queue: selectedQueue })}
            disabled={actionLoading === 'bulk'}
          />
          <PaiButton
            text={`Delete All (${jobs.length})`}
            startIcon={<DeleteIcon />}
            color="error"
            action={() => setConfirmDialog({ open: true, action: 'deleteAll', queue: selectedQueue })}
            disabled={actionLoading === 'bulk'}
          />
        </Stack>
      )}

      {/* DataGrid */}
      <Paper sx={{ flex: 1 }}>
        <DataGrid
          rows={jobs}
          columns={columns}
          density="compact"
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: 'finishedOn', sort: 'desc' }] },
          }}
          autoHeight
          localeText={{ noRowsLabel: 'No failed jobs in this queue' }}
          getRowId={(row) => row.id}
        />
      </Paper>

      {/* Confirm dialog for bulk actions */}
      <Dialog
        open={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
      >
        <DialogTitle>
          {confirmDialog?.action === 'retryAll' ? 'Retry All Failed Jobs?' : 'Delete All Failed Jobs?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog?.action === 'retryAll'
              ? `This will retry all ${jobs.length} failed job(s) in the ${QUEUE_NAMES.find(q => q.key === confirmDialog.queue)?.label} queue.`
              : `This will permanently remove all ${jobs.length} failed job(s) from the ${QUEUE_NAMES.find(q => q.key === confirmDialog?.queue)?.label} queue. This cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <PaiButton text="Cancel" action={() => setConfirmDialog(null)} />
          <PaiButton
            text={confirmDialog?.action === 'retryAll' ? 'Retry All' : 'Delete All'}
            color={confirmDialog?.action === 'deleteAll' ? 'error' : 'primary'}
            action={handleBulkAction}
            disabled={actionLoading === 'bulk'}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: '20px 24px 24px 48px',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    [theme.breakpoints.down('lg')]: {
      padding: '20px 8px 8px 8px',
    },
  },
}));

export default AdminQueueManagement;
